/**
 * Namespaces / ALH ↔ app locale bridge for ads.
 *
 * Mirrors the press-site pattern. The `Namespaces` enum maps the short name
 * used by `useTranslation()` to the actual JSON file name (and therefore the
 * ALH bundle name) on disk.
 *
 * `getObelixFriendlyLocale` resolves a Contentful `<lang>-<region>` locale
 * code (e.g. the value of `globalContext.locale` for the ads client) into
 * the ALH directory name that holds the matching translation file. The
 * resulting string is also the language key used to load `resources.json`
 * entries at runtime, so the bundler and the runtime share one source of
 * truth for the locale code.
 */
export enum Namespaces {
  common = "ads-common",
  nav = "ads-nav",
  footer = "ads-footer",
}

/**
 * Maps a Contentful locale code to the ALH-friendly locale directory name.
 *
 * Examples:
 *   "en-US" -> "en"
 *   "pt-BR" -> "pt"
 *   "es-ES" -> "es-ES"
 *   "zh-CN" -> "zh-CN"
 *   "zh-TW" -> "zh-TW"
 *   "fr-FR" -> "fr"  (default: take the language portion only)
 */
export const getObelixFriendlyLocale = (lang: string): string => {
  if (!lang) return "en";

  switch (lang) {
    case "en-US":
      return "en";
    case "pt-BR":
      return "pt";
    case "es-ES":
      return "es-ES";
    default:
      // Take the language portion only; ALH directories are lowercase.
      return (lang.split("-")[0] || lang).toLowerCase();
  }
};

/**
 * Returns the ALH namespaces that are always guaranteed to be loaded on
 * every page: `ads-common` (all chrome strings), plus the `ads-nav` and
 * `ads-footer` bundles that hold the navigation and footer display strings.
 */
export const includeDefaultNamespaces = (): Namespaces[] => [
  Namespaces.common,
  Namespaces.nav,
  Namespaces.footer,
];
